import {
  Component,
  HostListener,
  Inject,
  OnInit,
  PLATFORM_ID
} from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { TranslocoService } from '@ngneat/transloco';

import { OrderProfitService } from '../../../../services/order-profit';
import { ItemsService } from '../../../../services/items.service';

interface OrderProfitRow {
  id?: number | string;
  order_id?: number | string;
  order_number?: number | string;
  created_at?: string;

  customer_name?: string;
  client_name?: string;
  username?: string;
  user_id?: number | string;

  total?: number | string;
  total_amount?: number | string;
  selling_price?: number | string;
  revenue?: number | string;
  gross_sales?: number | string;

  cost?: number | string;
  cost_price?: number | string;
  total_cost?: number | string;
  products_cost?: number | string;

  profit?: number | string;
  net_profit?: number | string;

  invoice_discount?: number | string;
  partial_deduction?: number | string;
  other_deductions?: number | string;

  items_count?: number | string;
  total_quantity?: number | string;
  is_partially_completed?: boolean;

  [key: string]: unknown;
}

@Component({
  selector: 'app-ordersprofitmobile',
  templateUrl: './ordersprofitmobile.component.html',
  styleUrl: './ordersprofitmobile.component.scss'
})
export class OrdersprofitmobileComponent implements OnInit {
  isAr = false;
  isBrowser = false;

  warehouseId = 0;
  warehouses: any[] = [];

  profits: OrderProfitRow[] = [];

  contentReady = true;
  loadingMore = false;
  syncing = false;
  noData = false;

  errorMessage = '';

  currentPage = 1;
  lastPage = 1;
  perPage = 10;
  totalRecords = 0;
  hasMorePages = true;

  totalRevenue = 0;
  totalCost = 0;
  netProfit = 0;

  selectedYear = new Date().getFullYear();
  selectedMonth = new Date().getMonth() + 1;

  readonly years = Array.from(
    { length: 8 },
    (_, index) => new Date().getFullYear() - index
  );

  readonly months = [
    { value: 1, key: 'January' },
    { value: 2, key: 'February' },
    { value: 3, key: 'March' },
    { value: 4, key: 'April' },
    { value: 5, key: 'May' },
    { value: 6, key: 'June' },
    { value: 7, key: 'July' },
    { value: 8, key: 'August' },
    { value: 9, key: 'September' },
    { value: 10, key: 'October' },
    { value: 11, key: 'November' },
    { value: 12, key: 'December' }
  ];

  constructor(
    @Inject(PLATFORM_ID) private platformId: object,
    private orderProfitService: OrderProfitService,
    private itemsService: ItemsService,
    private transloco: TranslocoService
  ) {}

  ngOnInit(): void {
    this.isBrowser = isPlatformBrowser(this.platformId);

    if (this.isBrowser) {
      this.isAr = localStorage.getItem('language') === 'ar';
      this.warehouseId = Number(localStorage.getItem('posId')) || 0;
    }

    this.loadWarehouses();

    if (this.warehouseId) {
      this.resetAndLoadProfits();
    } else {
      this.contentReady = false;
      this.noData = true;
      this.errorMessage = this.transloco.translate('No warehouse selected');
    }
  }

  resetAndLoadProfits(): void {
    this.profits = [];
    this.currentPage = 1;
    this.lastPage = 1;
    this.totalRecords = 0;
    this.hasMorePages = true;
    this.noData = false;
    this.errorMessage = '';

    this.loadProfits(1, true);
  }

  loadProfits(page: number, firstLoad: boolean = false): void {
    if (!this.warehouseId) {
      this.contentReady = false;
      this.loadingMore = false;
      this.noData = true;
      this.errorMessage = this.transloco.translate('No warehouse selected');
      return;
    }

    if (firstLoad) {
      this.contentReady = true;
    } else {
      this.loadingMore = true;
    }

    this.errorMessage = '';

    this.orderProfitService
      .getProfitsByWarehouseMonthYear(
        this.warehouseId,
        this.selectedMonth,
        this.selectedYear,
        page,
        this.perPage
      )
      .subscribe({
        next: (response) => {
          const rows = this.extractRows(response);

          if (firstLoad) {
            this.profits = rows;
          } else {
            this.profits = [...this.profits, ...rows];
          }

          const payload =
            response?.data && !Array.isArray(response.data)
              ? response.data
              : response;

          this.currentPage = Number(
            response?.current_page ??
              payload?.current_page ??
              page
          );

          this.lastPage = Math.max(
            1,
            Number(
              response?.last_page ??
                payload?.last_page ??
                1
            )
          );

          this.totalRecords = Number(
            response?.total ??
              payload?.total ??
              this.profits.length
          );

          this.hasMorePages = this.currentPage < this.lastPage;
          this.noData = this.profits.length === 0;

          this.applySummary(response);

          this.contentReady = false;
          this.loadingMore = false;
        },
        error: (error) => {
          console.error(error);

          if (firstLoad) {
            this.profits = [];
            this.noData = true;
            this.contentReady = false;
          }

          this.loadingMore = false;
          this.errorMessage = this.transloco.translate(
            'Unable to load order profits'
          );
        }
      });
  }

  applyFilters(): void {
    this.resetAndLoadProfits();
  }

  syncProfits(): void {
    if (!this.warehouseId || this.syncing) {
      return;
    }

    this.syncing = true;
    this.errorMessage = '';

    this.orderProfitService
      .syncProfitsByWarehouse(this.warehouseId)
      .subscribe({
        next: () => {
          this.syncing = false;
          this.resetAndLoadProfits();
        },
        error: (error) => {
          console.error(error);
          this.syncing = false;
          this.errorMessage = this.transloco.translate(
            'Unable to recalculate profits'
          );
        }
      });
  }

  private loadWarehouses(): void {
    this.itemsService.getWearHouses().subscribe({
      next: (response: any) => {
        this.warehouses =
          response?.warehouse ??
          response?.data ??
          response ??
          [];

        if (!Array.isArray(this.warehouses)) {
          this.warehouses = [];
        }

        if (!this.warehouseId && this.warehouses.length) {
          this.warehouseId = Number(this.warehouses[0]?.id) || 0;

          if (this.warehouseId) {
            this.resetAndLoadProfits();
          }
        }
      },
      error: (error) => {
        console.error(error);
        this.warehouses = [];
      }
    });
  }

  @HostListener('window:scroll', [])
  onWindowScroll(): void {
    if (
      !this.isBrowser ||
      this.contentReady ||
      this.loadingMore ||
      !this.hasMorePages ||
      this.noData
    ) {
      return;
    }

    const scrollPosition =
      window.pageYOffset ||
      document.documentElement.scrollTop ||
      document.body.scrollTop ||
      0;

    const windowHeight = window.innerHeight;
    const pageHeight = document.documentElement.scrollHeight;

    const bottomReached =
      scrollPosition + windowHeight >= pageHeight - 220;

    if (bottomReached) {
      this.loadMoreProfits();
    }
  }

  loadMoreProfits(): void {
    if (this.loadingMore || !this.hasMorePages) {
      return;
    }

    const nextPage = this.currentPage + 1;
    this.loadProfits(nextPage, false);
  }

  rowRevenue(row: OrderProfitRow): number {
    return this.numberFrom(row, [
      'gross_sales',
      'revenue',
      'total_amount',
      'total',
      'selling_price',
      'sale_price'
    ]);
  }

  rowCost(row: OrderProfitRow): number {
    return this.numberFrom(row, [
      'products_cost',
      'cost',
      'total_cost',
      'cost_price',
      'purchase_price'
    ]);
  }

  rowDeductions(row: OrderProfitRow): number {
    return (
      this.numberFrom(row, ['invoice_discount']) +
      this.numberFrom(row, ['partial_deduction']) +
      this.numberFrom(row, ['other_deductions'])
    );
  }

  rowProfit(row: OrderProfitRow): number {
    const value = row.net_profit ?? row.profit;

    if (value !== undefined && value !== null && value !== '') {
      const parsed = Number(String(value).replace(/,/g, ''));
      return Number.isNaN(parsed) ? 0 : parsed;
    }

    return this.rowRevenue(row) - this.rowCost(row) - this.rowDeductions(row);
  }

  getUserName(row: OrderProfitRow): string {
    return String(
      row.username ||
        row.customer_name ||
        row.client_name ||
        row.user_id ||
        '-'
    );
  }

  getOrderId(row: OrderProfitRow): string {
    return String(row.order_number || row.order_id || row.id || '-');
  }

  trackByProfitId(index: number, row: OrderProfitRow): any {
    return row?.id || row?.order_id || index;
  }

  private extractRows(response: any): OrderProfitRow[] {
    const payload =
      response?.data && !Array.isArray(response.data)
        ? response.data
        : response;

    const rows =
      Array.isArray(response)
        ? response
        : response?.data ??
          payload?.data ??
          payload?.profits ??
          response?.profits ??
          [];

    return Array.isArray(rows) ? rows : [];
  }

  private applySummary(response: any): void {
    const payload =
      response?.data && !Array.isArray(response.data)
        ? response.data
        : response;

    const summary =
      response?.summary ??
      payload?.summary ??
      response?.totals ??
      payload?.totals ??
      response;

    const summaryRevenue = this.numberFrom(summary, [
      'total_gross_sales',
      'gross_sales',
      'total_revenue',
      'revenue',
      'total_sales',
      'sales'
    ]);

    const summaryCost = this.numberFrom(summary, [
      'total_products_cost',
      'products_cost',
      'total_cost',
      'cost',
      'total_expenses'
    ]);

    const summaryProfit = this.numberFrom(summary, [
      'total_net_profit',
      'net_profit',
      'total_profit',
      'profit'
    ]);

    this.totalRevenue =
      summaryRevenue ||
      this.profits.reduce((sum, row) => sum + this.rowRevenue(row), 0);

    this.totalCost =
      summaryCost ||
      this.profits.reduce((sum, row) => sum + this.rowCost(row), 0);

    this.netProfit =
      summaryProfit ||
      this.profits.reduce((sum, row) => sum + this.rowProfit(row), 0);
  }

  private numberFrom(source: any, keys: string[]): number {
    if (!source) {
      return 0;
    }

    for (const key of keys) {
      const value = source?.[key];

      if (value === null || value === undefined || value === '') {
        continue;
      }

      const parsed =
        typeof value === 'string'
          ? Number(value.replace(/,/g, ''))
          : Number(value);

      if (!Number.isNaN(parsed)) {
        return parsed;
      }
    }

    return 0;
  }
}